import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Shared plumbing for EDIT MODE on the native non-posting document screens
// (New Order / New Purchase Order / New Work Order / New Estimate). Each of
// those screens doubles as its own edit form: with an :id in the route it
// loads the document, prefills itself, and saves with PATCH instead of POST.
//
// The server is the authority on whether a document may be edited and on what
// (nativeDocEdit.js). Its verdict rides on the detail response as
// `editability` — { editable, mode: 'full' | 'restricted', reason, note,
// editableFields, lineFloors } — and these helpers render it and keep the two
// concurrency rules the API enforces:
//
//   * every save sends `expectedUpdatedAt`, the updated_at the screen loaded.
//     A second person saving first makes the PATCH match zero rows and the API
//     answers 409 — better a "reload and reapply" than a silent clobber.
//   * every kept line sends its `id`. Lines are reconciled by id server-side:
//     no id means a new line, and an existing id that is missing means delete.
//     Dropping the ids would delete and re-create every line, which the
//     shipped/received floors would then (correctly) refuse.

/**
 * Load a document for edit mode. Returns { loading, doc, editability, error }
 * and re-runs when the url changes. `pick` maps the API response onto the
 * shape the screen wants (each screen's detail payload differs).
 */
export function useEditDocument(url, pick) {
  const [state, setState] = useState({ loading: Boolean(url), doc: null, editability: null, error: null });

  useEffect(() => {
    if (!url) { setState({ loading: false, doc: null, editability: null, error: null }); return undefined; }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const { data } = await axios.get(url);
        if (cancelled) return;
        setState({ loading: false, doc: pick ? pick(data) : data, editability: data.editability || null, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          doc: null,
          editability: null,
          error: err.response?.data?.error || err.message,
        });
      }
    })();
    return () => { cancelled = true; };
    // pick is recreated per render on purpose — only the url should retrigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return state;
}

/**
 * The banner above an edit form: why the document cannot be edited, or what
 * the restricted mode allows. Renders nothing in full-edit mode.
 * @param {string} prefix  the screen's CSS class prefix (nat / npo / nwo / nia)
 */
export function EditabilityBanner({ editability, prefix }) {
  if (!editability) return null;
  if (!editability.editable) {
    return (
      <div className={`${prefix}-card`}>
        <p className={`${prefix}-sub`} style={{ margin: 0 }}>
          <strong>Not editable.</strong> {editability.reason}
        </p>
      </div>
    );
  }
  if (editability.mode === 'restricted' && editability.note) {
    return (
      <div className={`${prefix}-card`}>
        <p className={`${prefix}-sub`} style={{ margin: 0 }}>
          <strong>Limited edit.</strong> {editability.note}
        </p>
      </div>
    );
  }
  return null;
}

/**
 * A stale-write 409 deserves a different sentence than a validation error —
 * the operator's work is still on screen and only needs reapplying.
 */
export function saveErrorMessage(err) {
  const body = err.response?.data;
  if (body && body.code === 'stale_write') return body.error;
  if (body && body.partiallyApplied) return `${body.error} (part of the change was saved)`;
  return body?.error || err.message;
}
